import { secp256k1 } from "@noble/curves/secp256k1";
import { decodeBytes } from "../../primitives/encoding";

export type Hex = `0x${string}`;
export type PrivKey = Uint8Array;

export class Secp256k1 {
	private static readonly SECP256K1_DERIVATION_PATH = new Uint8Array([
		0x73, 0x65, 0x63, 0x70, 0x32, 0x35, 0x36, 0x6b, 0x31, 0x2d, 0x64, 0x65,
		0x72, 0x69, 0x76, 0x61, 0x74, 0x69, 0x6f, 0x6e, 0x2d, 0x70, 0x61, 0x74,
		0x68,
	]);

	/**
	 * Derive a private key from a seed using secp256k1 derivation
	 * @param seed - Seed bytes
	 * @returns Private key bytes
	 */
	async privateKeyFromSeed(seed: Uint8Array): Promise<PrivKey> {
		const secp256k1DerivationSeed = new Uint8Array(
			seed.length + Secp256k1.SECP256K1_DERIVATION_PATH.length,
		);
		secp256k1DerivationSeed.set(seed, 0);
		secp256k1DerivationSeed.set(
			Secp256k1.SECP256K1_DERIVATION_PATH,
			seed.length,
		);
		const privateKey = new Uint8Array(
			await crypto.subtle.digest("SHA-256", secp256k1DerivationSeed),
		);

		// An Ethereum private key must be an integer > 0 and < N (the order of the secp256k1 curve ~2^256-2^32-977).
		// The probability of a SHA256 hash being 0 or >= N is astronomically small.
		// Here, we handle that case
		if (!secp256k1.utils.isValidPrivateKey(privateKey)) {
			return this.privateKeyFromSeed(privateKey);
		}

		return privateKey;
	}

	/**
	 * Get public key from private key
	 * @param privateKey - Private key bytes
	 * @returns Public key bytes (uncompressed)
	 */
	getPublicKey(privateKey: PrivKey): Uint8Array {
		return secp256k1.getPublicKey(privateKey, false);
	}

	/**
	 * Sign a digest with a private key using secp256k1
	 * @param digest - 32-byte digest to sign (hex string or Uint8Array)
	 * @param privateKey - Private key bytes
	 * @returns Signature in hex format with recovery bit
	 */
	sign(digest: Hex | Uint8Array, privateKey: PrivKey): Hex {
		const digestBytes =
			typeof digest === "string" ? decodeBytes(digest.slice(2), "hex") : digest;
		if (digestBytes.length !== 32) {
			throw new Error("Digest must be 32 bytes");
		}
		const sig = secp256k1.sign(digestBytes, privateKey, { lowS: true });
		const r = this.toBigEndianHex(sig.r, 32);
		const s = this.toBigEndianHex(sig.s, 32);
		const v = sig.recovery ? "1c" : "1b";
		return `0x${r}${s}${v}` as Hex;
	}

	private toBigEndianHex(value: bigint, bytes: number) {
		return value.toString(16).padStart(bytes * 2, "0");
	}
}
